import { Component, ElementRef, ViewChild, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { BaseComponent } from '../../base.component';
import { AuthService, User } from '../../../../core/services/auth.service';
import { ButtonComponent } from '../button/button.component';
import { takeUntil } from 'rxjs';
import { DialogComponent } from '../dialog/dialog.component';
import { AlertComponent } from '../alert/alert.component';
import { ROUTES } from '../../../constants/routes.constants';
import { SearchStateService, TipoRicerca } from '../../../../core/services/search-state.service';
import { environment } from '../../../../../environments/environment';


@Component({
  selector: 'app-header',
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ButtonComponent,
    DialogComponent,
    AlertComponent,
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent extends BaseComponent {
  authService = inject(AuthService);
  router = inject(Router);
  searchStateService = inject(SearchStateService);


  public currentUser = signal<User | null>(null);

  isCompact = false;
  isDialogOpen = false;
  menuOpen = false;

  @ViewChild('menuBtn') private menuBtnRef?: ElementRef<HTMLButtonElement>;

  constructor() {
    super();
    this.initialization();
  }

  override ngOnInit(): void {
    super.ngOnInit();
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });
    this.onResize();
  }

  override ngOnDestroy(): void {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    super.ngOnDestroy();
  }

  initialization() {
    this.authService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        this.currentUser.set(user);
      });
  }

  openDialog() {
    if (!this.loggedIn) this.isDialogOpen = true;
  }

  closeDialog() {
    if (!this.loggedIn) this.isDialogOpen = false;
  }

  get loggedIn() {
    return !!this.currentUser();
  }

  get currentName() {
    const user = this.currentUser()!;
    return user.nome + ' ' + user.cognome;
  }

  /**
   * Fino a questa larghezza l'header è sempre compatto, cioè con il menu a comparsa:
   * sotto i 1025px la navigazione centrale è nascosta (vedi header.component.scss),
   * quindi l'hamburger deve esserci anche in cima alla pagina. Deve coincidere con
   * lo stesso valore in src/styles/global.scss (padding-top del body).
   */
  private readonly compactBreakpoint = 1024;

  onScroll = () => {
    const threshold = 60;

    if (window.innerWidth <= this.compactBreakpoint) {
      this.isCompact = true;
      return;
    }
    this.isCompact = window.scrollY > threshold;
  };

  onResize = () => {
    if (window.innerWidth <= this.compactBreakpoint) {
      this.isCompact = true;
    } else {
      // recalc based on scroll position when leaving mobile
      this.onScroll();
    }
  };

  toggleMenu() {
    this.menuOpen = !this.menuOpen;
  }

  /** Chiude il menu con Esc e riporta il focus sul pulsante che lo apre */
  onEscapeMenu() {
    if (!this.menuOpen) return;
    this.menuOpen = false;
    this.menuBtnRef?.nativeElement.focus();
  }

  openAccessFromMenu() {
    this.menuOpen = false;
    this.openDialog();
  }

  /** Voce del menu scelta: la navigazione è nel routerLink, qui si chiude solo il menu */
  closeMenu() {
    this.menuOpen = false;
  }

  // La navigazione verso la ricerca è affidata al routerLink dell'<a>:
  // qui si imposta solo il tipo di ricerca che la pagina leggerà dallo stato.
  selectSearch(tipo: TipoRicerca) {
    this.searchStateService.setTipo(tipo);
  }

  /** I due link di ricerca puntano alla stessa route: è il tipo a dire quale è "corrente" */
  isSearchCurrent(tipo: TipoRicerca): boolean {
    const path = this.router.url.split(/[?#]/)[0];
    return path === '/' + ROUTES.RICERCA && this.searchStateService.getState().tipo === tipo;
  }

  goToProfile() {
    throw new Error('Functionality not implemented yet');
  }

  accessoRiservato() {
    window.open(environment.shibbolethRuparUrl, '_blank', 'noopener,noreferrer');
  }

  accessoConsiglioRegionale() {
    window.open(environment.shibbolethConsiglioRegionaleUrl, '_blank', 'noopener,noreferrer');
  }
}
